import React from 'react';

import TruncatedCell from 'components/partials/table-component/truncated-cell/truncated-cell';
import Checkbox from 'components/partials/checkbox/checkbox';

import {
  FUND_TYPE_LABELS,
  FUND_SOURCE_LABELS,
  isFundTypeLabelKey,
  isFundSourceLabelKey,
} from '../funds-step.constants';

const commonHeaderStyles =
  'border-r-2 border-white font-semibold cursor-pointer';

type FundLike = { id: string | number; [key: string]: unknown };

type CellInfo = {
  renderValue: () => unknown;
  getValue: () => unknown;
  row: { original: FundLike };
};

type GetSelectFundsModalTableSettingsArgs = {
  isFundSelected: (id: string | number) => boolean;
  onToggle: (fund: FundLike) => void;
  onToggleAll: () => void;
  getAllSelected: () => boolean;
  getSomeSelected: () => boolean;
};

export const getSelectFundsModalTableSettings = ({
  isFundSelected,
  onToggle,
  onToggleAll,
  getAllSelected,
  getSomeSelected,
}: GetSelectFundsModalTableSettingsArgs) => [
  {
    id: 'checkbox',
    header: () => {
      const allSelected = getAllSelected();
      const someSelected = getSomeSelected();

      return (
        <Checkbox
          labelFor="select-all-funds"
          checkboxValue="selectAll"
          checked={allSelected}
          indeterminate={someSelected && !allSelected}
          onChange={() => onToggleAll()}
        />
      );
    },
    cell: (info: CellInfo) => {
      const fund = info.row.original;
      const isSelected = isFundSelected(fund.id);

      return (
        <Checkbox
          labelFor={`fund-${fund.id}`}
          checkboxValue={String(fund.id)}
          checked={isSelected}
          onChange={() => onToggle(fund)}
        />
      );
    },
    headerClassName: `${commonHeaderStyles} rounded-tl-md rounded-bl-md`,
    className: 'w-[4%] py-2 px-3',
    enableSorting: false,
  },
  {
    id: 'name',
    accessorKey: 'name',
    header: () => <span>FUND NAME</span>,
    cell: (info: CellInfo) => (
      <TruncatedCell value={info.renderValue() as string} />
    ),
    headerClassName: commonHeaderStyles,
    className: 'w-[50%] text-left font-semibold pl-2.5 py-2 max-w-0',
    enableSorting: true,
    sortingFn: 'alphanumeric',
  },
  {
    id: 'code',
    accessorKey: 'code',
    header: () => <span>CODE</span>,
    cell: (info: CellInfo) => info.renderValue() as React.ReactNode,
    headerClassName: commonHeaderStyles,
    className: 'w-[10%] pl-4 py-2',
    enableSorting: true,
  },
  {
    id: 'source',
    accessorKey: 'source',
    header: () => <span>SOURCE</span>,
    cell: (info: CellInfo) => {
      const value = String(info.getValue() ?? '');

      return isFundSourceLabelKey(value) ? FUND_SOURCE_LABELS[value] : value;
    },
    headerClassName: commonHeaderStyles,
    className: 'w-[12%] pl-4 py-2',
    enableSorting: true,
  },
  {
    id: 'type',
    accessorKey: 'type',
    header: () => <span>TYPE</span>,
    cell: (info: CellInfo) => {
      const value = String(info.getValue() ?? '');

      return isFundTypeLabelKey(value) ? FUND_TYPE_LABELS[value] : value;
    },
    headerClassName: commonHeaderStyles,
    className: 'w-[12%] pl-4 py-2',
    enableSorting: true,
  },
  {
    id: 'instAid',
    accessorKey: 'instAid',
    header: () => <span>INST. AID</span>,
    cell: (info: CellInfo) => (info.getValue() ? 'Yes' : 'No'),
    headerClassName: `${commonHeaderStyles} rounded-tr-md rounded-br-md`,
    className: 'w-[12%] pl-4 py-2',
    enableSorting: true,
  },
  {
    id: 'totalAid',
    accessorKey: 'totalAid',
    header: () => <span>TOTAL AID</span>,
    cell: (info: CellInfo) => (info.getValue() ? 'Yes' : 'No'),
    headerClassName: `${commonHeaderStyles} rounded-tr-md rounded-br-md`,
    className: 'w-[12%] pl-4 py-2',
    enableSorting: true,
  },
];
